import { supabase } from '../supabaseClient'

const _cache = {}
const CACHE_TTL_MS = 30_000

export function invalidateNucleusScope(userId) {
  delete _cache[`${userId}:0`]
  delete _cache[`${userId}:1`]
}

export async function getNucleusScope(userId, coordinatorMode = false) {
  const key = `${userId}:${coordinatorMode ? '1' : '0'}`
  const cached = _cache[key]
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) return cached.result
  const result = await _fetchNucleusScope(userId, coordinatorMode)
  _cache[key] = { result, timestamp: Date.now() }
  return result
}

async function _fetchNucleusScope(userId, coordinatorMode) {
  // Coordinator sees everything — no scope needed
  if (coordinatorMode) {
    return {
      allUsers: true,
      myParticipantId: null,
      nucleusIds: null,       // null = no filter
      coreNucleusIds: null,   // null = show all nucleus pills
      userIds: null,
      participantIds: null,
    }
  }

  const { data: myProfile } = await supabase
    .from('profiles').select('participant_id').eq('id', userId).single()
  const myParticipantId = myProfile?.participant_id

  if (!myParticipantId) {
    return { allUsers: false, myParticipantId: null, nucleusIds: [], coreNucleusIds: [], userIds: [userId], participantIds: [] }
  }

  // Fetch memberships with role — RLS on activity_sessions now enforces correct access
  // We only need nucleus IDs here for:
  //   1. Nucleus filter pills (coreNucleusIds = non-field-worker nuclei only)
  //   2. Session nucleus filter when pill is active
  const { data: memberships } = await supabase
    .from('nucleus_participants')
    .select('nucleus_id, role')
    .eq('participant_id', myParticipantId)
    .is('removed_at', null)

  const nucleusIds = (memberships || []).map(m => m.nucleus_id)
  // Pills only show for nuclei where user is core team (not field_worker)
  const coreNucleusIds = (memberships || [])
    .filter(m => m.role !== 'field_worker')
    .map(m => m.nucleus_id)

  // Fellow user IDs — needed for "sessions logged by any team member" queries
  // Only fetch for core team members (field workers don't need this)
  let userIds = [userId]
  if (coreNucleusIds.length > 0) {
    const { data: fellowMembers } = await supabase
      .from('nucleus_participants')
      .select('participant_id')
      .in('nucleus_id', coreNucleusIds)
      .is('removed_at', null)
    const fellowPids = (fellowMembers || []).map(m => m.participant_id).filter(Boolean)
    if (fellowPids.length > 0) {
      const { data: fellowProfiles } = await supabase
        .from('profiles').select('id').in('participant_id', fellowPids)
      userIds = [...new Set([userId, ...(fellowProfiles || []).map(p => p.id)])]
    }
  }

  return {
    allUsers: false,
    myParticipantId,
    nucleusIds,
    coreNucleusIds,
    userIds,
    participantIds: null, // RLS handles participant filtering — no manual list needed
  }
}
